import { useEffect, useState, type JSX } from 'react';

import { shaktiApi } from '../api/client';
import { useAuthStore } from '../stores/auth';
import { useProjectStore, type ProjectSummary } from '../stores/project';
import { NewProjectModal } from './NewProjectModal';

/**
 * Project switcher — F2.1 multi-app support (G2.1 round-3 hardening).
 *
 * Renders in the top nav. Lists every project the caller has access
 * to (scoped by their API key's organisation). Changing the
 * selection stores the new UUID in `projectStore`; every view
 * listening to `activeProjectId` re-fetches on change.
 *
 * On mount we call `GET /v1/projects?organization_id=<auth.org>`.
 * Round-2 shipped without threading the organisation id, so every
 * first-load fired `GET /v1/projects` with no query string and the
 * server 400'd on `missing organization_id`. G2.1 threads the id
 * from `useAuthStore` and skips the fetch entirely until auth has
 * resolved.
 */
export function ProjectSwitcher(): JSX.Element {
  const activeProjectId = useProjectStore((s) => s.activeProjectId);
  const projects = useProjectStore((s) => s.projects);
  const setActiveProject = useProjectStore((s) => s.setActiveProject);
  const setProjects = useProjectStore((s) => s.setProjects);
  const organizationId = useAuthStore((s) => s.organizationId);

  useEffect(() => {
    // G2.1 — wait for auth to resolve before asking the server for
    // projects. Without `organizationId` the handler now accepts the
    // X-Org-Id header (G2.1 server fallback), but the dashboard
    // proxy may strip it in some deploys, so we always thread the
    // id explicitly.
    if (!organizationId) {
      return;
    }
    const controller = new AbortController();
    (async () => {
      try {
        const list: ProjectSummary[] = await shaktiApi.listProjects({
          organizationId,
          signal: controller.signal,
        });
        setProjects(list);
      } catch (err) {
        // The switcher is additive, not blocking. Aborts happen on
        // unmount / orgId change; other errors (network, 5xx) leave
        // the store empty and the "No projects" branch renders.
        const aborted =
          err instanceof DOMException && err.name === 'AbortError';
        if (!aborted) {
          // eslint-disable-next-line no-console
          console.warn('[shakti] listProjects failed', err);
        }
      }
    })();
    return () => controller.abort();
  }, [setProjects, organizationId]);

  const [showNew, setShowNew] = useState(false);

  const emptyState = projects.length === 0;

  return (
    <div className="flex items-center gap-2" data-testid="project-switcher">
      <span className="text-2xs uppercase tracking-wider text-fg-muted">
        Project
      </span>
      {emptyState ? (
        <span className="text-xs text-fg-muted" data-testid="project-switcher-empty">
          No projects
        </span>
      ) : (
        <select
          className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-1 text-xs"
          value={activeProjectId ?? ''}
          onChange={(e) => setActiveProject(e.target.value || null)}
          data-testid="project-switcher-select"
        >
          {projects.map((p) => (
            <option key={p.id} value={p.id}>
              {p.name}
            </option>
          ))}
        </select>
      )}
      {/* H3.4 — "+" button always available, so a first-run user with
          zero projects can bootstrap from the same control. */}
      <button
        type="button"
        onClick={() => setShowNew(true)}
        aria-label="New project"
        title="New project"
        className="rounded-sm border border-border-subtle bg-bg-surface px-2 py-0.5 text-xs text-fg-primary hover:bg-bg-surface-2"
        data-testid="project-switcher-new"
      >
        +
      </button>

      {showNew ? (
        <NewProjectModal
          onClose={() => setShowNew(false)}
          onCreated={(p) => {
            setProjects([
              ...projects,
              {
                id: p.id,
                name: p.name,
                slug: p.slug,
                organization_id: organizationId ?? '',
              } as ProjectSummary,
            ]);
            setActiveProject(p.id);
            setShowNew(false);
          }}
        />
      ) : null}
    </div>
  );
}
